import {
  accountSearchColumns,
  boPhanSearchColumns,
  chiPhiKhongHopLeSearchColumns,
  kheUocSearchColumns,
  lenhSanXuatSearchColumns,
  lyDoNhapXuatSearchColumns,
  paymentInstallmentSearchColumns,
  phiSearchColumns,
  QUERY_KEYS,
  vatTuSearchColumns,
  viTriSearchColumns,
  vuViecSearchColumns,
  warehouseSearchColumns
} from '@/constants';
import {
  BoPhan,
  ChiPhiKhongHopLeData,
  ChiTietPhieuXuatKho,
  DotThanhToan,
  KheUoc,
  type KhoHang,
  LenhSanXuat,
  NhapXuat,
  type Phi,
  TaiKhoan,
  VatTu,
  ViTri,
  VuViec
} from '@/types/schemas';
import {
  CellFieldV2 as CellField,
  ConversionQuantityFieldV2,
  DvtFieldV2,
  SearchFieldV2 as SearchField
} from '@/components/custom/erp';
import { DEFAULT_MA_NX_CODE } from '../../../constants';
import { Checkbox } from '@/components/ui/checkbox';
import { calculateMoney } from '../hooks';

type CellParams = { row: ChiTietPhieuXuatKho; value?: unknown };
type LenhSanXuatLookup = LenhSanXuat & { ma_lsx?: string };

export const getDetailColumns = (
  onCellValueChange: (rowUuid: string | undefined, field: string, newValue: unknown) => void,
  fetchDetail: (params: string) => Promise<unknown>,
  hasInvoiceParams = false
) => {
  const disable = hasInvoiceParams;

  return [
    {
      field: 'ma_vt',
      headerName: 'Mã sản phẩm',
      width: 200,
      renderCell: (params: CellParams) => (
        <SearchField<VatTu>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
          searchColumns={vatTuSearchColumns}
          dialogTitle='Danh mục vật tư'
          columnDisplay='ma_vt'
          value={params.row.ma_vt_data?.ma_vt || params.row.ma_vt || ''}
          onRowSelection={async (row: VatTu) => {
            const data = await fetchDetail(row?.uuid || '');
            onCellValueChange(params.row.uuid, 'ma_vt_data', data);
          }}
          className='w-full'
          disabled={disable}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_vt',
        resultField: 'ma_vt_data',
        detailEndpoint: QUERY_KEYS.VAT_TU
      }
    },
    {
      field: 'ten_vt',
      headerName: 'Tên vật tư',
      width: 200,
      renderCell: (params: CellParams) => (
        <CellField name='ten_vt' value={params.row.ma_vt_data?.ten_vt || params.row.ten_vt || ''} disabled={disable} />
      ),
      nonWritable: true
    },
    {
      field: 'dvt',
      headerName: 'Đvt',
      width: 120,
      renderCell: (params: CellParams) => (
        <DvtFieldV2 row={params.row} onCellValueChange={onCellValueChange} disabled={disable} showConversionInEdit />
      ),
      nonWritable: true
    },
    {
      field: 'ma_kho',
      headerName: 'Mã kho',
      width: 150,
      renderCell: (params: CellParams) => (
        <SearchField<KhoHang>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.KHO_HANG}`}
          searchColumns={warehouseSearchColumns}
          dialogTitle='Danh mục kho'
          columnDisplay='ma_kho'
          value={params.row.ma_kho_data?.ma_kho || params.row.ma_vt_data?.ma_kho_data?.ma_kho || ''}
          onRowSelection={(row: KhoHang) => onCellValueChange(params.row.uuid, 'ma_kho_data', row)}
          disabled={disable}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_kho',
        resultField: 'ma_kho_data',
        detailEndpoint: QUERY_KEYS.KHO_HANG
      }
    },
    {
      field: 'ma_vi_tri',
      headerName: 'Mã vị trí',
      width: 150,
      renderCell: (params: CellParams) => (
        <SearchField<ViTri>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.VI_TRI}`}
          searchColumns={viTriSearchColumns}
          dialogTitle='Danh mục vị trí'
          columnDisplay='ma_vi_tri'
          value={params.row.ma_vi_tri_data?.ma_vi_tri || ''}
          onRowSelection={(row: ViTri) => {
            onCellValueChange(params.row.uuid, 'ma_vi_tri_data', row);
          }}
          disabled={disable}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_vi_tri',
        resultField: 'ma_vi_tri_data',
        detailEndpoint: QUERY_KEYS.VI_TRI
      }
    },
    {
      field: 'sl_ton',
      headerName: 'Tồn',
      width: 150,
      renderCell: (params: CellParams) => <CellField name='sl_ton' type='number' value={params.row.sl_ton || 0} />,
      nonWritable: true
    },
    {
      field: 'so_luong',
      headerName: 'Số lượng',
      width: 150,
      renderCell: (params: CellParams) => (
        <ConversionQuantityFieldV2
          row={params.row}
          onCellValueChange={onCellValueChange}
          disabled={disable}
          showConversionInEdit
        />
      )
    },
    {
      field: 'px_dd',
      headerName: 'Đích danh',
      width: 100,
      type: 'boolean',
      align: 'center',
      renderCell: (params: CellParams) => (
        <Checkbox
          checked={params.row.px_dd || false}
          disabled={disable}
          onCheckedChange={newValue => {
            onCellValueChange(params.row.uuid, 'px_dd', newValue);
          }}
        />
      ),
      nonWritable: true
    },
    {
      field: 'gia_nt',
      headerName: 'Giá VND',
      width: 150,
      renderCell: (params: CellParams) => (
        <CellField
          name='gia_nt'
          type='number'
          value={params.row.gia_nt || 0}
          disabled={!params.row.px_dd || disable}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'gia_nt', newValue)}
        />
      )
    },
    {
      field: 'tien_nt',
      headerName: 'Tiền VND',
      width: 180,
      renderCell: (params: CellParams) => (
        <CellField
          name='tien_nt'
          type='number'
          value={params.row.tien_nt || calculateMoney(params.row.gia_nt, params.row.so_luong)}
          disabled={!params.row.px_dd || disable}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'tien_nt', newValue)}
        />
      )
    },
    {
      field: 'tk_vt',
      headerName: 'Tk có',
      width: 100,
      renderCell: (params: CellParams) => (
        <CellField name='tk_vt' value={params.row.ma_vt_data?.tk_khcp_data?.code || ''} disabled={disable} />
      ),
      nonWritable: true
    },
    {
      field: 'ma_nx',
      headerName: 'Lý do xuất',
      width: 100,
      renderCell: (params: CellParams) => (
        <SearchField<NhapXuat>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.LY_DO_NHAP_XUAT}/`}
          searchColumns={lyDoNhapXuatSearchColumns}
          dialogTitle='Danh mục lý do nhập xuất'
          columnDisplay='ma_nx'
          value={params.row.ma_nx_data?.ma_nx || DEFAULT_MA_NX_CODE}
          onRowSelection={(row: NhapXuat) => {
            onCellValueChange(params.row.uuid, 'ma_nx_data', row);
          }}
          disabled={disable}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_nx',
        resultField: 'ma_nx_data',
        detailEndpoint: QUERY_KEYS.LY_DO_NHAP_XUAT
      }
    },
    {
      field: 'tk_du',
      headerName: 'TK nợ',
      width: 100,
      renderCell: (params: CellParams) => (
        <SearchField<TaiKhoan>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}`}
          searchColumns={accountSearchColumns}
          dialogTitle='Danh mục tài khoản'
          columnDisplay='code'
          value={params.row.tk_du_data?.code || ''}
          onRowSelection={(row: TaiKhoan) => {
            onCellValueChange(params.row.uuid, 'tk_du_data', row);
          }}
          disabled={disable}
        />
      ),
      pasteConfig: {
        lookupKey: 'code',
        resultField: 'tk_no_data',
        detailEndpoint: QUERY_KEYS.TAI_KHOAN
      }
    },
    {
      field: 'ma_bp',
      headerName: 'Bộ phận',
      width: 120,
      renderCell: (params: CellParams) => (
        <SearchField<BoPhan>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.BO_PHAN}`}
          searchColumns={boPhanSearchColumns}
          dialogTitle='Danh mục bộ phận'
          columnDisplay='ma_bp'
          value={params.row.ma_bp_data?.ma_bp || ''}
          onRowSelection={(row: BoPhan) => {
            onCellValueChange(params.row.uuid, 'ma_bp_data', row);
          }}
          disabled={disable}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_bp',
        resultField: 'ma_bp_data',
        detailEndpoint: QUERY_KEYS.BO_PHAN
      }
    },
    {
      field: 'ma_vv',
      headerName: 'Vụ việc',
      width: 120,
      renderCell: (params: CellParams) => (
        <SearchField<VuViec>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.VU_VIEC}`}
          searchColumns={vuViecSearchColumns}
          dialogTitle='Danh mục vụ việc'
          columnDisplay='ma_vu_viec'
          value={params.row.ma_vv_data?.ma_vu_viec || params.row.ma_vv_data?.ma_vv || ''}
          onRowSelection={(row: VuViec) => {
            onCellValueChange(params.row.uuid, 'ma_vv_data', row);
          }}
          disabled={disable}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_vu_viec',
        resultField: 'ma_vv_data',
        detailEndpoint: QUERY_KEYS.VU_VIEC
      }
    },
    {
      field: 'ma_dtt',
      headerName: 'Đợt thanh toán',
      width: 130,
      renderCell: (params: CellParams) => (
        <SearchField<DotThanhToan>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.DOT_THANH_TOAN}`}
          searchColumns={paymentInstallmentSearchColumns}
          dialogTitle='Đợt thanh toán'
          columnDisplay='ma_dtt'
          value={params.row.ma_dtt_data?.ma_dtt || ''}
          onRowSelection={(row: DotThanhToan) => {
            onCellValueChange(params.row.uuid, 'ma_dtt_data', row);
          }}
          disabled={disable}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_dtt',
        resultField: 'ma_dtt_data',
        detailEndpoint: QUERY_KEYS.DOT_THANH_TOAN
      }
    },
    {
      field: 'ma_ku',
      headerName: 'Khế ước',
      width: 120,
      renderCell: (params: CellParams) => (
        <SearchField<KheUoc>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.KHE_UOC}`}
          searchColumns={kheUocSearchColumns}
          dialogTitle='Danh mục khế ước'
          columnDisplay='ma_ku'
          value={params.row.ma_ku_data?.ma_ku || ''}
          onRowSelection={(row: KheUoc) => {
            onCellValueChange(params.row.uuid, 'ma_ku_data', row);
          }}
          disabled={disable}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_ku',
        resultField: 'ma_ku_data',
        detailEndpoint: QUERY_KEYS.KHE_UOC
      }
    },
    {
      field: 'ma_phi',
      headerName: 'Phí',
      width: 100,
      renderCell: (params: CellParams) => (
        <SearchField<Phi>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.PHI}`}
          searchColumns={phiSearchColumns}
          dialogTitle='Danh mục phí'
          columnDisplay='ma_phi'
          value={params.row.ma_phi_data?.ma_phi || ''}
          onRowSelection={(row: Phi) => {
            onCellValueChange(params.row.uuid, 'ma_phi_data', row);
          }}
          disabled={disable}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_phi',
        resultField: 'ma_phi_data',
        detailEndpoint: QUERY_KEYS.PHI
      }
    },
    {
      field: 'ma_sp',
      headerName: 'Sản phẩm',
      width: 160,
      renderCell: (params: CellParams) => (
        <SearchField<VatTu>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.VAT_TU}`}
          searchColumns={vatTuSearchColumns}
          dialogTitle='Danh mục sản phẩm'
          columnDisplay='ma_vt'
          value={params.row.ma_sp_data?.ma_vt || ''}
          onRowSelection={(row: VatTu) => {
            onCellValueChange(params.row.uuid, 'ma_sp_data', row);
          }}
          disabled={disable}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_vt',
        resultField: 'ma_sp_data',
        detailEndpoint: QUERY_KEYS.VAT_TU
      }
    },
    {
      field: 'ma_lsx',
      headerName: 'Lệnh sản xuất',
      width: 180,
      renderCell: (params: CellParams) => (
        <SearchField<LenhSanXuatLookup>
          searchEndpoint={`/${QUERY_KEYS.LENH_SAN_XUAT}`}
          searchColumns={lenhSanXuatSearchColumns}
          dialogTitle='Lệnh sản xuất'
          value={params.row.ma_lsx_data?.so_ct || ''}
          onRowSelection={(row: LenhSanXuatLookup) => {
            onCellValueChange(params.row.uuid, 'ma_lsx_data', row);
          }}
          disabled={disable}
        />
      )
    },
    {
      field: 'ma_cp0',
      headerName: 'C/p không h/lệ',
      width: 120,
      renderCell: (params: CellParams) => (
        <SearchField<ChiPhiKhongHopLeData>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.CHI_PHI_KHONG_HOP_LE}`}
          searchColumns={chiPhiKhongHopLeSearchColumns}
          dialogTitle='Chi phí không hợp lệ'
          columnDisplay='ma_cp_khl'
          value={params.row.ma_cp0_data?.ma_cpkhl || ''}
          onRowSelection={(row: ChiPhiKhongHopLeData) => {
            onCellValueChange(params.row.uuid, 'ma_cp0_data', row);
          }}
          disabled={disable}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_cpkhl',
        resultField: 'ma_cp0_data',
        detailEndpoint: QUERY_KEYS.CHI_PHI_KHONG_HOP_LE
      }
    },
    {
      field: 'so_phieu_yc',
      headerName: 'Số phiếu YC',
      width: 120,
      renderCell: (params: CellParams) => <CellField name='so_phieu_yc' value={params.value || ''} disabled />,
      nonWritable: true
    },
    {
      field: 'dong_yc',
      headerName: 'Dòng YC',
      width: 100,
      renderCell: (params: CellParams) => <CellField name='dong_yc' value={params.value || ''} disabled />,
      nonWritable: true
    }
  ];
};
